// ── fundamentalsData.js ─────────────────────────────────────────────────────
// Yahoo Finance v10 quoteSummary — live P/E, P/B, EV/EBITDA, Beta, etc.
// No API key needed. Cached 30 min. 3 at a time.
//
// Tries two fetch strategies:
//   1) /api/proxy (your Vercel proxy) — avoids CORS
//   2) Direct Yahoo fetch — fallback if proxy returns 401/403

import { proxyUrl } from "../hooks/useStockData";
import { authFetch } from "../lib/authFetch";

const MODULES  = ["price", "summaryDetail", "defaultKeyStatistics", "financialData"];
const CACHE_MS = 30 * 60 * 1000; // 30 minutes
const CONCURRENCY = 3;
const cache    = {};

const YAHOO_TICKER_ALIAS = { BRK: "BRK-B" };

// ── Two-strategy fetch ────────────────────────────────────
async function fetchWithFallback(url) {
  // Strategy 1: via proxy
  try {
    const res = await authFetch(proxyUrl(url));
    if (res.ok) return res;
    // If proxy returns 401/403/429, fall through to direct
    if (res.status === 401 || res.status === 403 || res.status === 429) {
      console.warn(`[fundamentalsData] Proxy returned ${res.status}, trying direct fetch`);
    } else {
      return res; // return other errors as-is
    }
  } catch { /* network error, try direct */ }

  // Strategy 2: direct (may hit CORS in some browsers, but works in many cases)
  try {
    const res = await fetch(url, {
      headers: {
        "User-Agent": "Mozilla/5.0",
        "Accept": "application/json",
      },
    });
    return res;
  } catch (err) {
    throw new Error(`Both proxy and direct fetch failed: ${err.message}`);
  }
}

async function fetchSummary(ticker) {
  const yahooTicker = YAHOO_TICKER_ALIAS[ticker] ?? ticker;
  const url = `https://query1.finance.yahoo.com/v10/finance/quoteSummary/${yahooTicker}?modules=${MODULES.join(",")}`;

  try {
    const res = await fetchWithFallback(url);
    if (!res.ok) return null;
    const json = await res.json();
    if (json?.quoteSummary?.error) return null;
    return json?.quoteSummary?.result?.[0] ?? null;
  } catch {
    return null;
  }
}

/** Map Yahoo quoteSummary result to our holding fundamentals. */
function mapSummaryToFundamentals(ticker, result) {
  if (!result) return null;
  const p  = result.price                ?? {};
  const s  = result.summaryDetail        ?? {};
  const ks = result.defaultKeyStatistics ?? {};
  const fi = result.financialData        ?? {};

  const raw = (obj, key) => obj?.[key]?.raw;

  const pe         = raw(s,  "trailingPE");
  const fwdPE      = raw(s,  "forwardPE");
  const pb         = raw(ks, "priceToBook");
  const evEbitda   = raw(ks, "enterpriseToEbitda");
  const beta       = raw(s,  "beta");
  const divYield   = raw(s,  "dividendYield");
  const profitMargin   = raw(fi, "profitMargins");
  const operatingMargin = raw(fi, "operatingMargins");
  const revenue        = raw(fi, "totalRevenue");
  const sharesOutstanding = raw(ks, "sharesOutstanding");
  const eps           = raw(ks, "trailingEps");
  const debtToEquity  = raw(fi, "debtToEquity");
  const roe           = raw(fi, "returnOnEquity");
  const freeCashFlow  = raw(fi, "freeCashflow");
  const totalDebt     = raw(fi, "totalDebt");
  const totalCash     = raw(fi, "totalCash");

  return {
    ticker,
    name:             p.longName ?? p.shortName ?? ticker,
    sector:           p.sector   ?? null,
    pe:               pe       != null ? round(pe,       1) : null,
    pb:               pb       != null ? round(pb,       2) : null,
    evEbitda:         evEbitda != null ? round(evEbitda, 1) : null,
    beta:             beta     != null ? round(beta,     2) : null,
    forwardPE:        fwdPE    != null ? round(fwdPE,    1) : null,
    dividend:         divYield != null ? round(divYield * 100, 2) : null,
    profitMargin:     profitMargin  != null ? round(profitMargin  * 100, 2) : null,
    operatingMargin:  operatingMargin != null ? round(operatingMargin * 100, 2) : null,
    revenue:          revenue             ?? null,
    sharesOutstanding: sharesOutstanding  ?? null,
    eps:              eps           != null ? round(eps, 2) : null,
    debtToEquity:     debtToEquity  != null ? round(debtToEquity, 2) : null,
    roe:              roe           != null ? round(roe * 100, 2) : null,
    freeCashFlow:     freeCashFlow  ?? null,
    totalDebt:        totalDebt     ?? null,
    totalCash:        totalCash     ?? null,
    price:            raw(p, "regularMarketPrice") ?? raw(p, "regularMarketOpen"),
    marketCap:        raw(p, "marketCap"),
  };
}

function round(n, d) {
  if (n == null || isNaN(n)) return null;
  return parseFloat(Number(n).toFixed(d));
}

/** Fetch fundamentals for one ticker. Uses 30min cache. */
export async function fetchFundamentals(ticker) {
  const cached = cache[ticker];
  if (cached && Date.now() - cached.ts < CACHE_MS) return cached.data;

  try {
    const result = await fetchSummary(ticker);
    const data   = mapSummaryToFundamentals(ticker, result);
    if (data) cache[ticker] = { data, ts: Date.now() };
    return data;
  } catch {
    return null;
  }
}

/** Run fn in batches of `concurrency`. */
async function runBatched(tickers, concurrency, fn) {
  const results = {};
  for (let i = 0; i < tickers.length; i += concurrency) {
    const batch        = tickers.slice(i, i + concurrency);
    const batchResults = await Promise.all(batch.map(t => fn(t)));
    batch.forEach((t, j) => { results[t] = batchResults[j]; });
  }
  return results;
}

/**
 * Fetch fundamentals for multiple tickers (3 at a time).
 * Returns { ticker: { pe, pb, evEbitda, ... } } — only successful fetches.
 */
export async function fetchFundamentalsBatch(tickers) {
  if (!tickers?.length) return {};
  const unique  = [...new Set(tickers)];
  const results = await runBatched(unique, CONCURRENCY, fetchFundamentals);
  return Object.fromEntries(
    Object.entries(results).filter(([, v]) => v != null)
  );
}

/** Clear cache (force refresh). */
export function clearFundamentalsCache() {
  Object.keys(cache).forEach(k => delete cache[k]);
}
